import { Location, LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationError, NavigationStart, Route, Router, RouterStateSnapshot, Routes, CanDeactivateFn } from '@angular/router';
import * as moment from 'moment';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApiService, AuthService, InsightsService, RentalInfo, SettingsService, TranslationService, UIService, ResultDetailLevel } from 't4core';
import * as uuid from 'uuid';
import { ApplicationContext, NavigationStep, NavType } from '../../Models/NavigationStep';
import { AppIntegrationService, AppSettingsService } from 't4-app-integration';
import { LayoutService } from '../LayoutService/layout.service';
import { BookingService } from '../BookingService/booking-service.service';
import { NavigationAction } from '../../Models/NavigationAction';
import { PageBase } from 't4core';
import { AppPageBase } from '../../Components/appPage.base';



export const StartBackRouteGuard: CanDeactivateFn<AppPageBase> = (component: AppPageBase, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot) => {
  return component.navigation?.isGoingBack ? false : true;
};

@Injectable({
  providedIn: 'root',
})
export class NavigationResolver  {
  private static _firstNavigation: boolean = true;
  constructor(
    private aviationService: NavigationService,
    private authService: AuthService,
    private Api: ApiService,
    private appSettings: AppSettingsService,
    private settings: SettingsService,
    private layoutService: LayoutService,
    private insights: InsightsService,
    private location: Location) { }

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<string> {
    // If no step is loaded or we are loading the reset view. Do nothing
    if (!this.aviationService.activeStep || route.url.toString().indexOf("ResetView") >= 0) {
      this.insights.trackTrace("Dead end in NavigationResolver", { url: route.url.toString() });

      // Reset visual elements
      this.layoutService.clear();

      this.aviationService.notifyStepLoaded();
      return;
    }

    if (this.aviationService.activeStep.Path != route.routeConfig.path) {
      //this.aviationService.setStepByPath(route.routeConfig.path)
    }

    // Determine the run context of the view we are loading
    var newContext: ApplicationContext = ApplicationContext.Owner;
    if (this.aviationService.activeStep.Context) newContext = this.aviationService.activeStep.Context;

    // Make sure the view will run in the correct context
    if (this.aviationService._activeContext != newContext || NavigationResolver._firstNavigation) {
      // When the first view is loaded its no longer the initial step
      NavigationResolver._firstNavigation = false;

      // ===== Customer context ===== ///
      // Make sure a user is authenticated and available in the app
      if (newContext == ApplicationContext.Customer) {
        if (this.appSettings.userId) { // If there is a user id available
          this.insights.setProperty("Context", this.appSettings.userId);
          this.insights.trackTrace("Switching to customer context", { step: this.aviationService.activeStep.Key });

          // Sign in as the customer user (UserName/Password are the same)
          await this.authService.Authenticate("", this.appSettings.userId, this.appSettings.userId);
        }
        else { // If user id is not available, sign out and reset
          // Log event
          this.insights.trackTrace("401 - Unauthenticated", { step: this.aviationService.activeStep.Key });

          // Reset user
          this.appSettings.setUser(null);

          // Fall back to main context
          this.insights.setProperty("Context", this.appSettings.contextId);
          this.insights.trackTrace("Falling back to owner context", { step: this.aviationService.activeStep.Key });
          await this.authService.Authenticate("", this.appSettings.contextId, this.appSettings.privateKey, this.appSettings.userId);

          // Navigate to app start page
          this.aviationService.navigateToView("Start");
        }
      }

      // ===== Regional context ===== //
      else if (newContext == ApplicationContext.Region && this.settings.clientSettings.RentalLocationId == null) {
        // Find the region based on selected location id
        var region = await this.getClosestParent(this.appSettings.localLocationId);
        if (region) {
          this.insights.setProperty("Context", region.Item1);
          this.insights.trackTrace("Switching to regional context", { step: this.aviationService.activeStep.Key, region: region.Item2 });
          await this.authService.Authenticate("", region.Item1, region.Item2, this.appSettings.userId);
        }
        else {
          // Fallback to  owner context
          this.insights.setProperty("Context", this.appSettings.contextId);
          this.insights.trackTrace("Falling back to owner context", { step: this.aviationService.activeStep.Key });
          await this.authService.Authenticate("", this.appSettings.contextId, this.appSettings.privateKey, this.appSettings.userId);
        }
      }

      // ===== Owner context ===== //
      else {
        this.insights.setProperty("Context", this.appSettings.contextId);
        this.insights.trackTrace("Switching to owner context", { step: this.aviationService.activeStep.Key });
        await this.authService.Authenticate("", this.appSettings.contextId, this.appSettings.privateKey, this.appSettings.userId);
      }

      // Save the new context
      this.aviationService._activeContext = newContext;
    }

    // Log page view
    var path = state.url;

    // Sched query string
    if (path.indexOf("?") > 0)
      path = path.substring(0, path.indexOf("?"));
    else if (path.indexOf(";") > 0)
      path = path.substring(0, path.indexOf(";"));

    // Reset visual elements
    this.layoutService.clear();

    this.insights.logPageView(path);
    this.aviationService.notifyStepLoaded();

    var backCommand = this.aviationService.activeStep.Actions.find(x => x.Command == "Back");
    if (backCommand) {
      var path = this.aviationService.getPath(backCommand.TargetStep)
      if (path != undefined) {
        this.location.replaceState(path, '', '');
      }
    }
  }

  // Cached regional contexts
  private regionalContexts: any[] = [];
  // Regonal contexts are the level below the owner context. They contain localized settings for that particular region
  // This method gets the regional context based on a rental location
  // The regional context returned will be the root of the leg containing the location
  public async getClosestParent(locationId: number): Promise<any> {
    // Validate input
    if (!locationId) return null;

    // Load from memory if available
    var ret = this.regionalContexts.find(x => x.LocationId == locationId);
    if (ret) return ret.Data;

    // Get regional context, cache and return
    var closestParent = await this.Api.get<any>("Account/FindClosestParent", { locationId: locationId, parentId: this.appSettings.contextId });
    this.regionalContexts.push({ LocationId: locationId, Data: closestParent });

    return closestParent;
  }
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  private static Steps: NavigationStep[] = [];
  private routes: Routes;

  private isInitialStep: boolean = true;
  public isGoingBack: boolean = false;
  public _activeContext: ApplicationContext = ApplicationContext.Owner;
  public _isInitialized: boolean = false;
  private history: NavigationStep[] = [];

  private isLoadingStep: boolean = false;

  public settingsParameter: string;

  private _activeStep: NavigationStep;
  public get activeStep(): NavigationStep { return this._activeStep; }
  private setActiveStep(step: NavigationStep) {
    if (!step) {
      this.insights.logError(new Error("Active step set to null"));
    }

    //var backCommand = step.Actions.find(x => x.Command == "Back");
    //if (backCommand) {
    //  var targetStep = NavigationService.Steps.find(x => x.Key == backCommand.TargetStep)
    //  if(targetStep)
    //    window.history.pushState('Test', '', targetStep.Path);
    //}

    this.insights.trackTrace("Active step changed: " + step.Key);
    this._activeStep = step;
  }

  // Converts the loaded NavigationSteps to routes that can be used in the angular routing engine (Should only be called from app.module)
  public static getRoutes(steps: NavigationStep[]): Routes {
    this.Steps = steps;
    let routes = steps.map<Route>(x => ({ path: x.Path, component: x.Component, data: { animation: x.Key }, resolve: { init: NavigationResolver } }));
    return routes;
  }

  public getPath(key: string): string {
    var targetStep = NavigationService.Steps.find(x => x.Key == key)
    if (targetStep)
      return targetStep.Path;

    return undefined;
  }


  // Load application navigation flows and store in memory
  private async LoadRoutes() {
    // Load routes either from context page or from theme file
    var steps = this.appSettings.getContextParam('routes');
    if (!steps) {
      let theme: string = this.appSettings.settings.overrideRoutes ? this.appSettings.settings.theme : 'Default';
      let path: string = `/Styles/${theme}/Routes/${this.appSettings.typeSettings.appType}.json?v=` + this.appSettings.clientVersion;

      steps = await this.http.get<NavigationStep[]>(path).toPromise();
    }

    // Store in memory
    NavigationService.Steps = steps;

    // Convert to routes and return
    this.routes = NavigationService.Steps.map<Route>(step => ({
      path: step.Path,
      data: { animation: step.Key },
      resolve: { init: NavigationResolver },
      component: AppSettingsService.components[step.Component.toString()],
      canDeactivate: step.Path == "" ? [StartBackRouteGuard] : []
    }));
  }

  // Finds the NavigationStep corresponding to a given relative path
  private findStepByPath(path: string): NavigationStep {
    // Detect parameters in path
    var len = -1;
    if (path.indexOf(";") > 0) len = path.indexOf(";");
    if (path.indexOf("?") > -1) len = path.indexOf("?");

    // Remove any parameters and try to find the step based on the path
    if (len >= 0)
      return NavigationService.Steps.find(x => x.Path == path.substring(0, len));
    else
      return NavigationService.Steps.find(x => x.Path == path);
  }

  constructor(
    public router: Router,
    private location: Location,
    private appService: AppIntegrationService,
    private Api: ApiService,
    private UI: UIService,
    public layoutService: LayoutService,
    zone: NgZone,
    private http: HttpClient,
    private appSettings: AppSettingsService,
    private insights: InsightsService,
    private bookingSvc: BookingService,
    private locationStrat: LocationStrategy
  ) {
    // Monitor navigation event to keep update step up to date
    this.router.events.subscribe(async x => {
      if (x instanceof NavigationStart) {
        const y = x as NavigationStart;

        // Mark whether this came from browser back/forward
        this.isGoingBack = (y.navigationTrigger === 'popstate');

        if (y.navigationTrigger === 'popstate') {
          // Clean the URL into a path
          let path = y.url;
          if (path.startsWith('/')) {
            path = path.substring(1);
          }
          const semicolonIndex = path.indexOf(';');
          if (semicolonIndex > 0) {
            path = path.substring(0, semicolonIndex);
          }

          const step = this.findStepByPath(path);
          if (step) {
            await this.gotoStep(step, {});
          }
        }
      }

      if (x instanceof NavigationCancel) {
        this.insights.trackTrace("Navigation cancelled!", { event: JSON.stringify(x) });
      }

      if (x instanceof NavigationError) {
        this.insights.logError(
          { name: "Navigation error!", message: "Navigation error!" },
          { event: JSON.stringify(x) }
        );
      }
    });

  }
  // Initialize the navigation service
  public async init() {
    // Load the routes
    await this.LoadRoutes();

    // Reset router configuration (Make sure it picks up the newly loaded routes)
    this.router.resetConfig(this.routes);

    // Reset history
    this.history = [];

    // If a step is not already loaded. Determine and load first view
    if (!this._activeStep) {
      var path = this.router.url.substring(1);
      await this.processInitialNavigation(path);
    }
  }

  // Process the things necessary to load the initial view.
  public async processInitialNavigation(path: string) {
    // Find the initial step based on the path
    let step: NavigationStep = this.findStepByPath(path);
    var params = {};

    // Parse parameters
    if (path.indexOf(";") > 0) {
      var qs = path.substring(path.indexOf(";") + 1);
      qs.split(";").map(i => {
        var pair = i.split("=");
        params[pair[0]] = pair[1];
      })
    }

    // If a matching step is not found
    // OR this is the default route ("/")
    // OR the step does not allow cold start (Supports starting at that view).
    if (!step || step.Path == "" || !step.AllowColdStart) {
      const userId: string = this.appSettings.userId;
      if (userId) {
        // Determine whit step to start from
        var action = await this.determineStartingStep();
        step = action.Step;
        params = action.Params;
      }
      else {
        // Find the default route
        step = NavigationService.Steps.find(x => x.Path == "");
      }
    }

    if (this.appSettings.params["targetStep"]) {
      const targetStep = NavigationService.Steps.find(x => x.Key == this.appSettings.params["targetStep"]);
      if (targetStep != null) step = targetStep;
    }
    // Execute the new step
    await this.executeInitialNavigation(step, params);
  }

  // Determine which view to load when the user opens the app based on the situation
  private async determineStartingStep(): Promise<NavigationAction> {
    // Find all active or future bookings
    const activeOrFutureBookings: RentalInfo[] = await this.Api.get<RentalInfo[]>("Rental/App/GetMyActiveAndFutureBookings", null, null, null, ResultDetailLevel.Basic);
    const upcomingBookings = activeOrFutureBookings?.filter(rental => moment.duration(rental.PickupTime.diff(moment.now())).hours() <= 2);
    const activeBookings = activeOrFutureBookings?.filter(rental => rental.Status == 100 || rental.Status == 150);

    // If the user has an active booking, open that (User is currently renting)
    if (activeBookings && activeBookings.length === 1) {
      let tmp = NavigationService.Steps.find(x => x.Path == "Rental/InFlight");
      return { Step: tmp, Params: { rentalId: activeBookings[0].Id } };
    }
    // If the user has 1, ond only 1, future booking. open that.
    else if (upcomingBookings && upcomingBookings.length === 1) {
      let tmp = NavigationService.Steps.find(x => x.Path == "Rental/BookingInfo");
      return { Step: tmp, Params: { rentalId: upcomingBookings[0].Id } };
    }
    // If there are more than one future booking. Show that list.
    else if (activeOrFutureBookings && activeOrFutureBookings.length > 0) {
      var tmp = NavigationService.Steps.find(x => x.Path == "MyPage/Overview");
      return { Step: tmp, Params: {} };
    }
    // Else we load the default route
    else {
      var tmp = NavigationService.Steps.find(x => x.Path == "");
      return { Step: tmp, Params: {} };
    }
  }

  // Invoke the routing system by navigating to the selected step
  private async executeInitialNavigation(step: NavigationStep, params: any) {
    // Get parameters
    //let params = action.Params;
    //if (!params) params = this.appSettings.params;

    // If rentalId is specified initialize services
    //if (params && params["rentalId"]) {
    //  await this.bookingSvc.restartSession(parseInt(this.appSettings.params["rentalId"]));
    //}

    // Set active step
    await this.setActiveStep(step);

    // Clear layout elements
    this.layoutService.clear();


    // Apply default route params if they are not already set
    if (step.Params) {
      for (var p in step.Params)
        if (!params[p]) params[p] = step.Params[p];
    }

    // Navigate  to the view with or without 
    if (params && Object.keys(params).length > 0 && step.Path != "") {
      await this.router.navigate([step.Path, params]);
    }
    else {
      await this.router.navigate([step.Path]);
    }
  }

  // Checks if authentication is required for a given view
  public isAuthenticationRequired(key: string): boolean {
    var step = NavigationService.Steps.find(x => x.Key == key);
    if (step && step.Context) {
      return step.Context === ApplicationContext.Customer;
    }
    else return false;
  }



  // Execute a command on the current view, taking  the user to the next view
  public async executeCommand(command: string, params: any = {}, isGoingBack: boolean = false, source: string = null) {
    console.log(source + " => " + command);

    if (this.isLoadingStep) {
      this.insights.logError(new Error("Command failed. Loading step."), { command: command, step: this.activeStep.Key });
      return;
    }

    // Store direction information
    if (!this.activeStep) {
      this.insights.logError(new Error("ActiveStep is null: ExecuteCommand"), { command: command, Initialized: this.appService.Device.IsInitialized.toString() });
    }
    if (this.activeStep.ConfirmOnLeave && (this.activeStep.ConfirmOnLeave.DisplayOn.find(x => x as NavType == NavType.NormalNavigation as NavType) > -1)) {
      var confirm = await this.UI.confirm(this.activeStep.ConfirmOnLeave.TitleKey, this.activeStep.ConfirmOnLeave.TitleText, this.activeStep.ConfirmOnLeave.ContentKey, this.activeStep.ConfirmOnLeave.ContentText);
      if (!confirm) return;
    }
    this.isGoingBack = isGoingBack;

    // Log command
    this.insights.logEvent('Executing: ' + command, { Params: JSON.stringify(params) });

    //Find the requested action
    var action = this.activeStep.Actions.find(x => x.Command == command);
    if (action) {

      // Find the step we are going to navigate to
      var nextStep = NavigationService.Steps.find(x => x.Key == action.TargetStep);
      if (nextStep) {
        this.gotoStep(nextStep, params);
      }
    } else {
      this.insights.logError({ name: "Command not found: " + command, message: this.activeStep.Key + " does not have an action named " + command }, { stack: new Error().stack, source: source });
    }
  }

  // Load a specified navigation step
  private async gotoStep(nextStep: NavigationStep, params: any = {}): Promise<boolean> {
    const isBackNav = this.isGoingBack === true;

    // Handle any "confirm on leave" settings for the current view
    if (!isBackNav &&
      this.activeStep?.ConfirmOnLeave &&
      this.activeStep.ConfirmOnLeave.DisplayOn.find(x => x as NavType == NavType.NormalNavigation as NavType) > -1) {

      const confirm = await this.UI.confirm(
        this.activeStep.ConfirmOnLeave.TitleKey,
        this.activeStep.ConfirmOnLeave.TitleText,
        this.activeStep.ConfirmOnLeave.ContentKey,
        this.activeStep.ConfirmOnLeave.ContentText
      );
      if (!confirm) return false;
    }

    // If we are navigating to the same view (refresh) - cancel
    if (!isBackNav && this.activeStep?.Path === nextStep.Path) {
      return false;
    }

    // If we are moving forward we need to add a step to the history in order for the back button to work correctly
    if (!isBackNav && this.activeStep && !this.activeStep.PreventHistory) {
      const last = this.history[this.history.length - 1];
      if (!last || last.Key !== this.activeStep.Key) {
        this.history.push(this.activeStep);
      }
    }

    this.isLoadingStep = true;

    // Set the target step as the active one
    this.setActiveStep(nextStep);

    // Load parameters from step specification
    if (!params) params = {};
    if (this.activeStep.Params) {
      for (const key of Object.keys(this.activeStep.Params)) {
        if (params[key] === undefined) {
          params[key] = this.activeStep.Params[key];
        }
      }
    }

    // If no params exists set to null to avoid routing problems
    if (!params || Object.keys(params).length <= 0) params = null;

    // Navigate to the new view with or without parameters

    if (!params) {
      this.router.navigate([this.activeStep.Path], { skipLocationChange: this.activeStep.PreventHistory });
    } else {
      this.router.navigate([this.activeStep.Path, params], { skipLocationChange: this.activeStep.PreventHistory });
    }
    return true;
  }


  public notifyStepLoaded() {
    this.isLoadingStep = false;
  }

  //Navigation from the menu
  //Menu navigation allows routing to any view
  public async navigateToView(command: string, params: any = {}) { 
    // Store direction
    this.isGoingBack = false;

    // Log the navigation event
    this.insights.logEvent('Navigating: ' + command, {...params});

    // Find and navigate to the requested step
    var nextStep = NavigationService.Steps.find(x => x.Key == command);
    if (nextStep) {
      this.gotoStep(nextStep, params);
    }
  }

  //Reload current route
  public async reload(params: any = {}) {
    var route = this.activeStep.Path;
    this.router.navigateByUrl('/Reset/ReloadView', { skipLocationChange: true }).then(() =>
      this.router.navigate([route, params]));
  }

  // Retrieves the value of a given parameter
  public getParam(param: string): string {

    //Angular query paremeter structure
    if (this.router.url.indexOf(";") > -1) {
      var params: string = this.router.url.substring(this.router.url.indexOf(";") + 1, this.router.url.length + 1);
      var paramlist: string[] = params.split(";");
      for (let p of paramlist) {
        if (p.indexOf(param + "=") !== -1) {
          //Parameter found, returns value
          var paramValue = p.split("=")[1];
          if (paramValue && paramValue.length > 0) {
            return paramValue;
          }
        }
      }
    }

    //Normal query parameter structure
    if (this.router.url.indexOf("?") > -1) {
      var params: string = this.router.url.substring(this.router.url.indexOf("?") + 1, this.router.url.length + 1);
      var paramlist: string[] = params.split("&");
      for (let p of paramlist) {
        if (p.indexOf(param + "=") !== -1) {
          //Parameter found, returns value
          var paramValue = p.split("=")[1];
          if (paramValue && paramValue.length > 0) {
            return paramValue;
          }
        }
      }
    }

    // Stored as input parameter to the app?
    if (this.appSettings.params && this.appSettings.params[param]) {
      return this.appSettings.params[param];
    }

    // Static parameter defined on route?
    if (this.activeStep.Params && this.activeStep.Params[param]) {
      return this.activeStep.Params[param];
    }

    //Parameter not found
    return null;
  }

  public async back() {
    console.log("Back pressed, activeStep:", this.activeStep?.Key);

    if (this.activeStep?.ConfirmOnLeave && this.activeStep.ConfirmOnLeave.DisplayOn.includes(NavType.BackNavigation)) {
      const confirm = await this.UI.confirm(this.activeStep.ConfirmOnLeave.TitleKey, this.activeStep.ConfirmOnLeave.TitleText, this.activeStep.ConfirmOnLeave.ContentKey, this.activeStep.ConfirmOnLeave.ContentText);
      if (!confirm) return;
    }

    // 1. Try to use the explicit Back command from our JsonFile.
    const backAction = this.activeStep?.Actions.find(a => a.Command === "Back");
    if (backAction) {
      const targetStep = NavigationService.Steps.find(
        s => s.Key === backAction.TargetStep
      );

      if (targetStep) {
        console.log("Using Back command to go to:", targetStep.Key);
       
        const last = this.history[this.history.length - 1];
        if (last && last.Key === targetStep.Key) {
          console.log("Popping duplicate target from history:", last.Key);
          this.history.pop();
        }

        this.isGoingBack = true;
        await this.gotoStep(targetStep);
        return;
      }
    }
    console.log(this.history);
    // 2. Optional: fall back to our logical history stack.
    if (this.history.length > 0) {
      const previous = this.history.pop();
      console.log(previous);
      console.log("No Back command, using custom history to go to:", previous.Key);
      this.isGoingBack = true;
      await this.gotoStep(previous);
      return;
    }

    // 3. Last-resort fallback: browser back
    console.log("No Back command and no custom history, falling back to browser back");
    this.isGoingBack = true;
    window.history.back();
    return;

    // Does active step have a defined back action?
    var backCommand = this.activeStep.Actions.find(x => x.Command == "Back");
    if (backCommand) {
      if (backCommand.TargetStep != "Stay") {
        // Get the last item of the history (Put it back afterwards to enable correct processing below)
        var pop = this.history.pop();
        this.history.push(pop);

        console.log(this.history);

        // Find the step from the back command
        var next = NavigationService.Steps.find(x => x.Key == backCommand.TargetStep);

        // Go through history until we find the target step, removing one by one
        while (pop && next && pop.Path != next.Path) {
          pop = this.history.pop();
        }

        // Then extecute the back command on the active step
        await this.executeCommand("Back", null, true);
      }
    }
    else // If no back-command is specified, use history
    {
      window.history.back();
    }
  }
}
